import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldAlert,
  CheckCircle2,
  ExternalLink,
  ArrowLeft,
  Crosshair,
  AlertOctagon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const publicClient = axios.create({ baseURL: API });

// Each row maps directly to what the live a11y check on the homepage
// confirmed (title / lang / h1 / landmarks / labels / alt-text / skip link /
// noscript fallback). When the site changes, re-audit and update this list.
const PASSED_CRITERIA = [
  {
    code: "1.1.1",
    level: "A",
    name: "Non-text Content",
    detail: "Every image on the site exposes meaningful alt text. Decorative icons are explicitly marked aria-hidden.",
  },
  {
    code: "1.3.1",
    level: "A",
    name: "Info and Relationships",
    detail: "Semantic landmarks (<header>, <main>, <footer>) wrap content. Form inputs are paired with <label> elements; heading hierarchy starts at a single <h1>.",
  },
  {
    code: "2.4.1",
    level: "A",
    name: "Bypass Blocks",
    detail: "A keyboard-focusable 'Skip to main content' link is the first focusable element on every page. Press Tab once to reveal it.",
  },
  {
    code: "2.4.2",
    level: "A",
    name: "Page Titled",
    detail: "Every page exposes a descriptive <title> in the HTML head — readable by both browsers and assistive technology.",
  },
  {
    code: "2.4.6",
    level: "AA",
    name: "Headings and Labels",
    detail: "Headings and form labels describe topic or purpose. No empty or duplicate-text headings.",
  },
  {
    code: "2.4.7",
    level: "AA",
    name: "Focus Visible",
    detail: "Every interactive element renders a visible focus ring (Tailwind ring-2 utility) when reached via keyboard.",
  },
  {
    code: "3.1.1",
    level: "A",
    name: "Language of Page",
    detail: "<html lang=\"en\"> is set so screen readers pronounce content using English phonetics.",
  },
  {
    code: "3.3.2",
    level: "A",
    name: "Labels or Instructions",
    detail: "Every form field on /schedule and the hero scan widget has an associated label and inline placeholder hint.",
  },
  {
    code: "4.1.2",
    level: "A",
    name: "Name, Role, Value",
    detail: "All buttons and links expose an accessible name. SVG-only icons inside interactive elements use aria-label.",
  },
];

function Badge({ level }) {
  const color = level === "AA" ? "bg-blue-700 text-white" : "bg-zinc-200 text-zinc-800";
  return (
    <span className={`inline-flex items-center text-[10px] font-bold tracking-wider px-1.5 py-0.5 ${color}`}>
      WCAG {level}
    </span>
  );
}

export default function CompliancePage() {
  const [audit, setAudit] = useState(null);

  useEffect(() => {
    document.title = "Compliance Verification — Silent Shield";
    publicClient
      .get("/public/self-audit-status")
      .then((r) => setAudit(r.data))
      .catch(() => setAudit(null));
    // Update OG/meta tags dynamically for this route so LinkedIn / Slack /
    // email previews render the seal narrative instead of the homepage's.
    const pairs = [
      ["meta[name='description']", "content", "Verified WCAG 2.1 AA — Silent Shield passes its own audit. View the criteria, methodology, and live compliance status."],
      ["meta[property='og:title']", "content", "Verified WCAG 2.1 AA — Silent Shield"],
      ["meta[property='og:description']", "content", "Silent Shield audits its own site with the same engine it deploys to protect customers. View the criteria and live status."],
      ["meta[property='og:url']", "content", "https://adainterceptor.com/compliance"],
      ["link[rel='canonical']", "href", "https://adainterceptor.com/compliance"],
    ];
    const prev = {};
    pairs.forEach(([sel, attr, val]) => {
      const el = document.querySelector(sel);
      if (el) {
        prev[sel] = { attr, val: el.getAttribute(attr) };
        el.setAttribute(attr, val);
      }
    });
    return () => {
      // Restore on unmount so homepage OG cards aren't poisoned.
      Object.entries(prev).forEach(([sel, { attr, val }]) => {
        const el = document.querySelector(sel);
        if (el && val !== null) el.setAttribute(attr, val);
      });
    };
  }, []);

  const lastAudit = audit?.last_audit_at
    ? new Date(audit.last_audit_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="compliance-page">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-blue-700 focus:text-white focus:px-4 focus:py-2 focus:font-bold focus:tracking-wider focus:uppercase focus:text-sm focus:ring-2 focus:ring-blue-700 focus:ring-offset-2"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 border-2 border-white grid place-items-center">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">
                Silent Shield · Compliance
              </div>
              <div className="font-display font-black tracking-tight text-base">
                ADA INTERCEPTOR
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-white"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
        </div>
      </header>

      <main id="main" className="max-w-4xl mx-auto px-6 py-10 sm:py-14">
        {/* Trust seal block */}
        <section
          className="bg-white border-2 border-zinc-950 p-6 sm:p-8 shadow-[8px_8px_0_0_rgba(29,78,216,1)]"
          data-testid="compliance-seal-block"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-shrink-0">
              <div
                className="w-20 h-20 border-2 border-zinc-950 bg-zinc-950 text-white grid place-items-center relative"
                role="img"
                aria-label="Silent Shield Verified seal"
              >
                <CheckCircle2 className="w-9 h-9 text-blue-400" strokeWidth={2} />
                <div className="absolute -bottom-2 -right-2 bg-blue-700 text-white text-[9px] font-bold tracking-wider px-1.5 py-0.5">
                  AA
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold tracking-[0.28em] text-blue-700 uppercase">
                Silent Shield Verified
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1 leading-tight">
                This site passes WCAG 2.1 AA.
              </h1>
              <p className="mt-2 text-sm sm:text-base text-zinc-700 leading-relaxed">
                Verified by the same engine we deploy to protect our customers.
                Last automated audit:{" "}
                <span className="font-mono font-bold text-zinc-950">{lastAudit}</span>.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { stat: PASSED_CRITERIA.length, label: "Criteria passing" },
              { stat: PASSED_CRITERIA.filter((c) => c.level === "AA").length, label: "AA-level" },
              { stat: "0", label: "Open findings" },
            ].map((s) => (
              <div key={s.label} className="border-2 border-zinc-950 p-3">
                <div className="font-display text-3xl font-black tabular-nums">{s.stat}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mt-0.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Criteria list */}
        <section className="mt-10" data-testid="compliance-criteria-list">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-500 uppercase">
                The Criteria
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-2">
                What we audit against.
              </h2>
            </div>
            <a
              href="https://www.w3.org/WAI/WCAG21/quickref/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-700 hover:text-blue-700"
            >
              WCAG 2.1 Reference <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <ul className="space-y-3">
            {PASSED_CRITERIA.map((c) => (
              <li
                key={c.code}
                className="bg-white border-2 border-zinc-950 p-4 sm:p-5 grid grid-cols-[auto_1fr] gap-4 sm:gap-6 items-start"
                data-testid={`compliance-row-${c.code.replace(/\./g, "-")}`}
              >
                <div className="flex flex-col items-center gap-1 min-w-[60px]">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                  <Badge level={c.level} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">{c.code}</span>
                    <span className="font-display text-base sm:text-lg font-bold tracking-tight">
                      {c.name}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-zinc-700 leading-relaxed">{c.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Method block */}
        <section className="mt-12 bg-white border-2 border-zinc-950 p-6 sm:p-8" data-testid="compliance-method">
          <div className="text-[10px] font-bold tracking-[0.28em] text-zinc-500 uppercase">
            Methodology
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight mt-2">
            How we know.
          </h2>
          <ul className="mt-4 space-y-3 text-sm text-zinc-700 leading-relaxed">
            <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
              <Crosshair className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
              <div>
                <span className="font-bold text-zinc-950">Automated DOM audit.</span>{" "}
                Our internal compliance engine renders the live site in a headless browser
                and inspects landmark roles, label associations, heading hierarchy, focus
                handling, alt-text presence, and ARIA usage on every interactive element.
              </div>
            </li>
            <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
              <Crosshair className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
              <div>
                <span className="font-bold text-zinc-950">Static-HTML sanity check.</span>{" "}
                The same audit runs against the pre-JavaScript HTML to confirm titles,
                meta descriptions, language declaration, and noscript fallbacks survive
                non-JavaScript scanners — the same kind used by predatory legal bots.
              </div>
            </li>
            <li className="grid grid-cols-[auto_1fr] gap-3 items-start">
              <Crosshair className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
              <div>
                <span className="font-bold text-zinc-950">Continuous re-verification.</span>{" "}
                Every deployment triggers a fresh audit. Any new violation re-opens this page
                with the specific finding flagged — the green seal is not static, it is earned.
              </div>
            </li>
          </ul>
        </section>

        {/* CTAs */}
        <section className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/"
            className="bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-sm py-4 text-center transition-colors"
            data-testid="compliance-scan-cta"
          >
            Scan Your Own Site
          </Link>
          <Link
            to="/schedule"
            className="bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-sm py-4 text-center transition-colors"
            data-testid="compliance-schedule-cta"
          >
            Schedule 10 Minute Call
          </Link>
        </section>
      </main>

      <footer className="bg-white border-t border-zinc-200 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-6 text-[11px] text-zinc-500 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} ADA Interceptor. All rights reserved.</span>
          <a href="mailto:support@adainterceptor.com" className="hover:text-blue-700">
            support@adainterceptor.com
          </a>
        </div>
      </footer>
    </div>
  );
}
